import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { HELP_PAGE_PATH } from "../../Components/App/Home/HelpCard";
import usePartnerAdvice, { PARTNER_ADVICE_STUDENT_THRESHOLD } from "../../Hooks/usePartnerAdvice";
import type { OnboardingStep } from "../../Hooks/Dashboard/useHome";
import { getSupportContact } from "../../config/supportContact";
import { capitalizeFirstLetter } from "../../Utils/Function";
import { isVerifiedTrainer } from "../../Utils/VerifiedTrainerAmounts";
import { DV2 } from "../DeclarationV2/theme";
import { plural } from "../DeclarationV2/format";
import { vocab } from "../DeclarationV2/vocabulary";
import { Button, Card, TextLink, useDv2Theme } from "../DeclarationV2/layout/Ui";
import { buildStartSummary, startProgressLabel, StartStep } from "./startSteps";

interface Props {
    firstName?: string | null;
    steps: OnboardingStep[];
    overallProgress: number;
    /** Nombre d'élèves validés, pour le texte de la carte partenaire. */
    totalStudents: number;
    /** Compte validé par l'équipe (profil terminé). */
    profileReady: boolean;
    loading?: boolean;
    /** Lien discret vers le tableau de bord chiffré, comme l'onglet « Tableau de bord » d'aujourd'hui. */
    onShowDashboard?: () => void;
}

/**
 * Page « Démarrage » du prof ou du coach (maquette 11a) : les quatre étapes
 * dans l'ordre où on les fait, la seule étape en cours mise en avant, et à
 * droite « Comment ça marche », l'aide et l'offre partenaire. Les règles de
 * complétion et de verrouillage sont celles du hook `useHome`, inchangé ;
 * l'ancien `OnboardingTab` reste dans le code. Même page pour les trois
 * marques : couleur et nom de marque par `useDv2Theme`, mots par `vocab`
 * (élève ou client, cours ou prestation), offre partenaire seulement chez
 * Teach'r, pas d'avance immédiate ni d'exemple chiffré chez Verified Trainer.
 */
const StartV2 = ({ firstName, steps, overallProgress, totalStudents, profileReady, loading = false, onShowDashboard }: Props) => {
    const navigate = useNavigate();
    const { primary, brandName } = useDv2Theme();
    const partner = usePartnerAdvice();
    const contact = getSupportContact();
    const isVt = isVerifiedTrainer();
    const [exampleOpen, setExampleOpen] = useState(false);
    /** Bloc choisi par le prof : bordure et bouton en couleur de marque. Aucun par défaut. */
    const [selectedId, setSelectedId] = useState<string | null>(null);

    const summary = useMemo(() => buildStartSummary(steps, brandName), [steps, brandName]);
    const progress = startProgressLabel(summary);
    const barWidth = Math.max(0, Math.min(100, overallProgress));

    const numberBadge = (step: StartStep) => {
        const base: React.CSSProperties = { width: 36, height: 36, borderRadius: "50%", display: "grid", placeItems: "center", fontWeight: 800, fontSize: 15, flexShrink: 0 };
        if (step.completed) return <div aria-hidden style={{ ...base, background: DV2.successBg, color: DV2.success, border: `2px solid ${DV2.success}` }}>✓</div>;
        if (selectedId === step.id) return <div aria-hidden style={{ ...base, background: primary, color: "#fff" }}>{step.number}</div>;
        return <div aria-hidden style={{ ...base, background: "#fff", color: DV2.placeholder, border: `2px solid #C9D3E0` }}>{step.number}</div>;
    };

    const checkbox = (done: boolean) => (
        <span aria-hidden style={{ width: 18, height: 18, borderRadius: 5, flexShrink: 0, marginTop: 1, display: "grid", placeItems: "center", fontSize: 12, fontWeight: 800, border: `2px solid ${done ? DV2.success : "#C9D3E0"}`, background: done ? DV2.success : "#fff", color: "#fff" }}>{done ? "✓" : ""}</span>
    );

    const stepCard = (step: StartStep) => {
        const selected = selectedId === step.id;
        const select = () => setSelectedId(step.id);
        return (
            <section
                key={step.id}
                className="transition-colors"
                style={{ background: DV2.surface, borderRadius: DV2.radius, padding: "20px 22px", border: selected ? `2px solid ${primary}` : `1px solid ${DV2.border}`, margin: selected ? 0 : 1, display: "flex", gap: 16, alignItems: "flex-start", cursor: "pointer" }}
                aria-current={step.current ? "step" : undefined}
                onClick={select}
            >
                {numberBadge(step)}
                <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 6 }}>
                    <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
                        <h2 style={{ margin: 0 }}>
                            <button
                                type="button"
                                aria-pressed={selected}
                                onClick={select}
                                className="text-left"
                                style={{ background: "none", border: "none", padding: 0, fontFamily: "inherit", fontWeight: 800, fontSize: 16, color: step.completed ? DV2.muted : DV2.text, textDecoration: step.completed ? "line-through" : "none", cursor: "pointer" }}
                            >
                                {step.title}
                            </button>
                        </h2>
                        <span style={{ fontSize: 12, color: step.completed ? DV2.success : DV2.placeholder, whiteSpace: "nowrap", fontWeight: step.completed ? 800 : 400 }}>{step.hint}</span>
                    </div>
                    {!step.completed && <p style={{ fontSize: 14, color: DV2.textSoft, lineHeight: 1.5, margin: 0 }}>{step.description}</p>}
                    {!step.completed && step.checklist.length > 0 && (
                        <ul style={{ listStyle: "none", margin: "6px 0 0", padding: "14px 16px", background: DV2.pageBg, borderRadius: DV2.radiusSm, display: "flex", flexDirection: "column", gap: 8 }}>
                            {step.checklist.map((item) => (
                                <li key={item.id} style={{ display: "flex", gap: 10, alignItems: "flex-start", fontSize: 13 }}>
                                    {checkbox(item.done)}
                                    <span>
                                        <b style={{ color: item.done ? DV2.muted : DV2.text }}>{item.title}</b>
                                        {item.hint && <span style={{ color: DV2.muted }}>, {item.hint}</span>}
                                        {item.done && <span className="sr-only">, fait</span>}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    )}
                    {step.waiting && (
                        <div style={{ marginTop: 6, fontSize: 13, color: DV2.textSoft, lineHeight: 1.5, padding: "10px 14px", background: DV2.tint, borderRadius: DV2.radiusSm }}>{step.waiting}</div>
                    )}
                    {(step.action || step.secondary) && (
                        <div className="flex flex-wrap items-center gap-3" style={{ marginTop: 6 }}>
                            {step.action && <Button label={step.action.label} variant={selected ? "primary" : "secondary"} size="sm" onClick={step.action.run} />}
                            {step.secondary && <Button label={step.secondary.label} variant="secondary" size="sm" onClick={step.secondary.run} />}
                        </div>
                    )}
                </div>
            </section>
        );
    };

    const howItWorks = isVt
        ? `Vous ${vocab.giveVerb()} ${vocab.aCourse()}, vous ${vocab.theCourse() === "la prestation" ? "la" : "le"} déclarez ici. Votre ${vocab.client()} règle sa part, et ${brandName} vous verse votre rémunération.`
        : `Vous ${vocab.giveVerb()} ${vocab.aCourse()} à domicile, vous ${vocab.theCourse() === "la prestation" ? "la" : "le"} déclarez ici. Votre ${vocab.client()} ne paie que la moitié grâce à l'avance immédiate du crédit d'impôt, l'URSSAF règle l'autre moitié, et ${brandName} vous verse la totalité.`;

    const partnerText = profileReady
        ? `Votre profil est prêt, il ne manque plus que les ${vocab.clients()}. Si vous n'avez pas encore ${vocab.ofAClient()}, un partenaire vous partage des méthodes concrètes pour décrocher vos premières heures de ${vocab.course()}.`
        : `Dès que votre profil est validé, un partenaire vous partage des méthodes concrètes pour décrocher vos premières heures de ${vocab.course()}. Objectif, ${PARTNER_ADVICE_STUDENT_THRESHOLD} ${vocab.clients()}.`;

    return (
        <div className="flex flex-col gap-6">
            <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
                <div className="min-w-0">
                    <h1 style={{ fontSize: 26, fontWeight: 800, margin: 0, lineHeight: 1.2 }}>Bienvenue {capitalizeFirstLetter(firstName ?? "")}</h1>
                    <div style={{ fontSize: 14, color: DV2.muted, marginTop: 4, maxWidth: 680 }}>
                        Quatre étapes avant {vocab.yourFirstCourse()} {vocab.declared()}. Comptez une quinzaine de minutes, vous pouvez vous arrêter et reprendre quand vous voulez.
                    </div>
                </div>
                {onShowDashboard && <TextLink label="Voir mon tableau de bord" onClick={onShowDashboard} />}
            </header>

            <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_340px] gap-6 items-start">
                <div className="flex flex-col gap-3 min-w-0">
                    <div className="flex items-center justify-between gap-3" style={{ fontSize: 13, color: DV2.muted, padding: "0 4px" }}>
                        <span><b style={{ color: DV2.text }}>{progress.step}</b>, {progress.detail}</span>
                        <span>{summary.completedCount} sur {summary.total}</span>
                    </div>
                    <div role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={overallProgress} aria-label="Avancement du démarrage" style={{ height: 6, borderRadius: 3, background: "#E3EAF2", overflow: "hidden", margin: "0 4px" }}>
                        <div style={{ width: `${barWidth}%`, height: "100%", background: primary, transition: "width .4s" }} />
                    </div>

                    {loading ? (
                        <div className="animate-pulse flex flex-col gap-3" aria-busy="true" aria-label="Chargement du démarrage">
                            {[0, 1, 2, 3].map((i) => <div key={i} style={{ height: 96, borderRadius: DV2.radius, background: "#E3EAF2" }} />)}
                        </div>
                    ) : summary.steps.map(stepCard)}
                </div>

                <aside className="flex flex-col gap-4 min-w-0" aria-label="Pour vous aider">
                    {partner.visible && (
                        <Card tint padding={22}>
                            <div style={{ fontSize: 12, fontWeight: 800, color: primary, textTransform: "uppercase", letterSpacing: ".05em" }}>Avec notre partenaire</div>
                            <h2 style={{ fontWeight: 800, fontSize: 15, margin: "10px 0 0" }}>Trouver vos premiers {vocab.clients()}</h2>
                            <p style={{ fontSize: 13, color: DV2.textSoft, lineHeight: 1.55, margin: "10px 0 0" }}>{partnerText}</p>
                            <div className="flex flex-wrap items-center gap-4" style={{ marginTop: 10 }}>
                                <TextLink label="Ça m'intéresse" onClick={partner.openPartnerPage} />
                                <TextLink label="Plus tard" size={12} onClick={partner.snooze} />
                            </div>
                            {totalStudents > 0 && (
                                <div style={{ fontSize: 12, color: DV2.muted, marginTop: 8 }}>{plural(totalStudents, vocab.client(), vocab.clients())} sur {PARTNER_ADVICE_STUDENT_THRESHOLD} pour l'instant.</div>
                            )}
                        </Card>
                    )}

                    <Card padding={22}>
                        <h2 style={{ fontWeight: 800, fontSize: 15, margin: 0 }}>Une question, un blocage</h2>
                        <p style={{ fontSize: 13, color: DV2.textSoft, lineHeight: 1.55, margin: "12px 0 0" }}>
                            Un conseiller {brandName} vous répond en général dans la journée, par e-mail{contact.phone ? " ou par téléphone" : ", ou vous rappelle si vous préférez"}.
                        </p>
                        <div className="flex flex-wrap gap-2" style={{ marginTop: 12 }}>
                            <Button label="Nous écrire" variant="secondary" size="sm" onClick={() => navigate(HELP_PAGE_PATH())} />
                            <Button label="Être rappelé" variant="secondary" size="sm" onClick={() => navigate(HELP_PAGE_PATH(), { state: { callback: true } })} />
                        </div>
                    </Card>

                    <Card padding={22}>
                        <h2 style={{ fontWeight: 800, fontSize: 15, margin: 0 }}>Comment ça marche</h2>
                        <p style={{ fontSize: 13, color: DV2.textSoft, lineHeight: 1.55, margin: "12px 0 0" }}>{howItWorks}</p>
                        {!isVt && (
                            <div style={{ marginTop: 12 }}>
                                <TextLink label={exampleOpen ? "Masquer l'exemple" : "Voir un exemple chiffré"} onClick={() => setExampleOpen((o) => !o)} />
                                {exampleOpen && (
                                    <p style={{ fontSize: 13, color: DV2.textSoft, lineHeight: 1.55, margin: "10px 0 0", padding: "10px 14px", background: DV2.pageBg, borderRadius: DV2.radiusSm }}>
                                        {capitalizeFirstLetter(vocab.aCourse())} de 2 heures à 30 € de l'heure, soit 60 €. Votre {vocab.client()} règle 30 €, l'URSSAF verse les 30 € restants grâce à l'avance immédiate, et {brandName} vous verse les 60 € moins ses frais de gestion, le mois suivant.
                                    </p>
                                )}
                            </div>
                        )}
                    </Card>
                </aside>
            </div>
        </div>
    );
};

export default StartV2;
